import React, { useRef, useCallback } from 'react';
import { Box, Text, Flex, HStack, Spinner, Textarea, IconButton } from '@chakra-ui/react';
import { ArrowUpIcon } from '@chakra-ui/icons';
import ReactMarkdown from 'react-markdown';

/**
 * Message bubble component
 * Handles rendering of user, assistant, and system messages
 * Supports inline editing for user messages
 * 
 * @param {Object} props
 * @param {Object} props.message - Message object
 * @param {boolean} props.isEditing - Whether this message is being edited
 * @param {string} props.editContent - Edit content (when editing)
 * @param {Function} props.onEditChange - Edit content change handler
 * @param {Function} props.onEditSubmit - Submit edit handler
 * @param {Function} props.onEditCancel - Cancel edit handler
 * @param {Function} props.onStartEdit - Start editing handler
 * @param {React.Ref} props.editBubbleRef - Ref for edit bubble (for click-outside detection)
 * @param {React.Ref} props.editTextareaRef - Ref for edit textarea
 */
const MessageBubble = React.memo(({ 
  message, 
  isEditing, 
  editContent, 
  onEditChange, 
  onEditSubmit, 
  onEditCancel,
  onStartEdit,
  editBubbleRef,
  editTextareaRef
}) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const bubbleRef = useRef(null);
  
  // Handle keyboard shortcuts for edit textarea
  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onEditSubmit(message);
    }
    // Shift+Enter allows new line (default behavior)
  }, [message, onEditSubmit]);

  return (
    <Box mb={6}>
      {isUser ? (
        // User messages styled as bubbles with edit capability
        <Flex 
          justify="flex-end" 
          align="flex-end" 
          gap={2}
        >
          <Box
            ref={(node) => {
              if (isEditing) {
                editBubbleRef.current = node;
              }
              bubbleRef.current = node;
            }}
            maxW="80%"
            minW={isEditing ? "300px" : "auto"}
            bg="#2a2a2a"
            color="#fafafa"
            px={4}
            py={3}
            border="1px solid"
            borderColor={isEditing ? "rgba(255,255,255,0.30)" : "rgba(255,255,255,0.10)"}
            borderRadius="10px"
            borderTopRightRadius="4px"
            position="relative"
            cursor={isEditing ? "text" : "pointer"}
            transition="all 0.2s"
            _hover={isEditing ? {} : {
              borderColor: "rgba(255,255,255,0.22)",
              bg: "#323232",
              transform: "translateY(-1px)",
            }}
            onClick={(e) => {
              if (!isEditing && onStartEdit) {
                onStartEdit(message, e);
              }
            }}
          >
            {isEditing ? (
              <Textarea
                ref={editTextareaRef}
                value={editContent}
                onChange={onEditChange}
                onKeyDown={handleEditKeyDown}
                bg="transparent"
                border="none"
                color="#fafafa"
                _hover={{}}
                _focus={{ outline: "none", boxShadow: "none" }}
                resize="none"
                minH="40px"
                maxH="400px"
                overflowY="auto"
                fontSize="sm"
                lineHeight="1.5"
                p={0}
                w="100%"
                autoFocus
              />
            ) : (
              <Text fontSize="sm" lineHeight="1.5" whiteSpace="pre-wrap">
                {message.content}
              </Text>
            )}
          </Box>
          
          {isEditing && (
            <IconButton
              icon={<ArrowUpIcon />}
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (editContent?.trim()) {
                  onEditSubmit(message);
                }
              }}
              isDisabled={!editContent?.trim()}
              aria-label="Save & Continue"
              borderRadius="full"
              flexShrink={0}
              tabIndex={0}
              bg="#fafafa"
              color="#0a0a0a"
              _hover={{ opacity: 0.85 }}
              _disabled={{ opacity: 0.3, cursor: 'not-allowed' }}
              _focus={{ boxShadow: 'none' }}
              _focusVisible={{ boxShadow: 'none' }}
            />
          )}
        </Flex>
      ) : (
        // AI/Assistant messages displayed directly (like Cursor)
        <Box pl={4} pr={2}>
          {message.isLoading ? (
            <HStack spacing={2} mb={3}>
              <Spinner size="sm" color="rgba(255,255,255,0.4)" />
              <Text fontSize="sm" color="#a3a3a3">Thinking...</Text>
            </HStack>
          ) : (
            <>
              <Box mb={3}>
                <ReactMarkdown
                  components={{
                    p: props => <Box {...props} as="p" fontSize="sm" lineHeight="1.7" whiteSpace="pre-wrap" color="#fafafa" mb={3}/>,
                    h1: props => <Box {...props} as="h1" fontSize="xl" fontWeight="700" letterSpacing="-0.02em" color="#fafafa" mb={3}/>,
                    h2: props => <Box {...props} as="h2" fontSize="lg" fontWeight="600" letterSpacing="-0.01em" color="#fafafa" mb={3}/>,
                    h3: props => <Box {...props} as="h3" fontSize="md" fontWeight="600" letterSpacing="-0.01em" color="#fafafa" mb={2}/>,
                    ul: props => <Box {...props} as="ul" pl={4} mb={3}/>,
                    ol: props => <Box {...props} as="ol" pl={4} mb={3}/>,
                    li: props => <Box {...props} as="li" fontSize="sm" lineHeight="1.7" color="#fafafa" mb={1}/>,
                    code: props => <Box {...props} as="code" fontFamily="mono" bg="rgba(255,255,255,0.06)" px={1} borderRadius="4px" color="#fafafa" border="1px solid rgba(255,255,255,0.08)"/>,
                    blockquote: props => <Box {...props} as="blockquote" pl={4} borderLeft="2px solid rgba(255,255,255,0.15)" color="#a3a3a3" mb={3}/>
                  }}
                >
                  {typeof message.content === 'string' ? message.content : (message.content ? JSON.stringify(message.content, null, 2) : '')}
                </ReactMarkdown>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
});

export default MessageBubble;

